import React from "react";
import PropTypes from "prop-types";

import GenericCard from "components/UI/GenericCard/GenericCard";

import { toFixed } from "utils/helpers";
import {
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
} from "recharts";

const ProfitHistoryChart = (props) => {
  const { transactions } = props;
  // add every transaction profit to total and save date and total to data array, use reduce for it
  const data = transactions
    .slice()
    .reverse()
    .reduce((acc, transaction) => {
      const { closeDate, netProfit } = transaction;
      const accProfit = acc.length ? acc[acc.length - 1].accProfit : 0;
      const total = accProfit + netProfit.value;
      return [
        ...acc,
        {
          closeDate,
          accProfit: toFixed(total),
          displayDate: new Date(closeDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          fullDate: closeDate,
        },
      ];
    }, []);

  const gradientOffset = () => {
    if (!data.length) return 0.5;
    const dataMax = Math.max(...data.map((i) => i.accProfit));
    const dataMin = Math.min(...data.map((i) => i.accProfit));

    if (dataMax <= 0) {
      return 0;
    }
    if (dataMin >= 0) {
      return 1;
    }

    return dataMax / (dataMax - dataMin);
  };

  const off = data.length > 0 ? gradientOffset() : 0.5;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const pnlValue = data.accProfit;
      const isPositive = pnlValue >= 0;

      return (
        <div className="p-3 border border-gray-600 rounded-lg bg-[#111827] shadow-lg">
          <p className="text-gray-300 text-sm">
            {new Date(data.fullDate).toLocaleDateString()}
          </p>
          <p
            className={`font-semibold ${
              isPositive ? "text-green-400" : "text-red-400"
            }`}
          >
            Total P&L: ${pnlValue}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <GenericCard
      title="Profit over time"
      maxHeight={650}
      className="table-card [&>.content]:p-0 [&>.content]:overflow-hidden [&>.content]:h-full"
    >
      <ResponsiveContainer width="100%" height="100%" minHeight={650}>
        <AreaChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <defs>
            <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
              <stop offset={off} stopColor="#10b981" stopOpacity={0.2} />
              <stop offset={off} stopColor="#ef4444" stopOpacity={0.2} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#374151"
            strokeOpacity={1}
          />
          <XAxis
            dataKey="displayDate"
            tick={{ fontSize: 12 }}
            stroke="#9ca3af"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="#9ca3af"
            tickFormatter={(value) => `$${value}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            y={0}
            stroke="#6b7280"
            strokeDasharray="2 2"
            strokeOpacity={0.6}
          />
          <Area
            type="monotone"
            dataKey="accProfit"
            stroke="#ffffff"
            strokeOpacity={0.4}
            strokeWidth={1.5}
            fill="url(#splitColor)"
            isAnimationActive={false}
            dot={false}
            activeDot={{
              r: 6,
              stroke: "#10b981",
              strokeWidth: 2,
              fill: "#10b981",
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </GenericCard>
  );
};

ProfitHistoryChart.propTypes = { transactions: PropTypes.array };

export default ProfitHistoryChart;
